import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Banknote,
  CheckCircle2,
  Clock,
  CreditCard,
  Info,
  Loader2,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { useInvoices, type InvoiceRow } from '@/hooks/useInvoices';
import { InfoDot } from '@/components/ui/InfoDot';
import { useStripeMethods, useRemoveStripeMethod, useStripePay } from '@/hooks/useStripeMethods';
import { stripeMethodLabel } from '@/lib/stripe';
import { useActingAs } from '@/hooks/useActingAs';
import { useGroupInvoices } from '@/hooks/useGroupInvoices';
import ChainPay from '@/pages/pay/ChainPay';
import { SelfPayPanel } from '@/components/pay/SelfPayPanel';
import { AchAgreementCard } from '@/components/pay/AchAgreementCard';
import { fmtMirrorDate } from '@/lib/dates';
import { usePendingPayments, processingNote } from '@/hooks/usePendingPayments';

// Card surcharge nudge: gently steer large balances to ACH.
const CARD_NUDGE_OVER = 2500;

function fmtDate(iso: string): string {
  return fmtMirrorDate(iso);
}

/**
 * /pay — select open invoices, pay with a saved Stripe card/bank.
 * (Stripe is the only payment rail — the Bill & Pay bridge and the legacy
 * QBO-direct tokenized flow were removed 2026-08-12.)
 */
export default function Pay() {
  const navigate = useNavigate();
  const { data: invoices, isLoading } = useInvoices();
  const { isActingAs, customerName } = useActingAs();
  // A chain's accounting login pays every store from here, not just the
  // master's own invoices. Same wrapper the Invoices page uses — and it must
  // stay ABOVE this page's own hooks-free work but BELOW the hook calls, so
  // hook order never changes between renders.
  const { data: group, isLoading: groupLoading } = useGroupInvoices();

  // Prefix a charge confirm with the acting-as customer when staff are
  // operating inside a customer's account (safety net — never charge the
  // wrong customer by mistake).
  const actingPrefix = (msg: string) =>
    isActingAs ? `You are acting as ${customerName}.\n\n${msg}` : msg;

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Stripe rail: saved cards/banks + one-click pay.
  const stripeMethodsQuery = useStripeMethods(true);
  const stripeMethods = stripeMethodsQuery.data?.methods ?? [];
  // Banks typed in by hand, still waiting on Stripe's microdeposit code. They
  // are deliberately NOT in `stripeMethods` — an unverified bank cannot be
  // charged — so without this the customer sees no bank at all and no reason.
  const pendingMethods = stripeMethodsQuery.data?.pending ?? [];
  const removeStripe = useRemoveStripeMethod();
  const stripePay = useStripePay();
  const [stripeMethodId, setStripeMethodId] = useState<string | null>(null);
  const [stripeNotice, setStripeNotice] = useState<string | null>(null);
  useEffect(() => {
    if (stripeMethodId == null && stripeMethods.length > 0) {
      setStripeMethodId(stripeMethods.find((m) => m.is_default)?.stripe_pm_id ?? stripeMethods[0]!.stripe_pm_id);
    }
  }, [stripeMethodId, stripeMethods]);
  const stripeMethod = stripeMethods.find((m) => m.stripe_pm_id === stripeMethodId) ?? null;

  // Open, payable invoices.
  //
  // ⚠ A RENTAL ON AUTOPAY IS STILL PAYABLE HERE. This used to exclude them on
  //   the reasoning that autopay would charge them — but enrolment is not a
  //   charge, and when autopay is off (or a card fails) the exclusion left the
  //   customer looking at a balance with no way to clear it. Las Palmas carried
  //   $77.50 like that (2026-09-15). If it is open and showing, it is payable.
  //
  //   Paying one twice is guarded on the other side: autopay_due_invoices skips
  //   an invoice with a payment in flight.
  //
  // ⚠ AN INVOICE WITH A PAYMENT STILL CLEARING IS NOT PAYABLE HERE (2026-10-22).
  //   A bank (ACH) payment takes 3–5 business days and the QuickBooks mirror
  //   reads the invoice open the whole time, so it used to sit in this list
  //   inviting a second payment. It is listed separately as "clearing", and
  //   stripe-pay-invoices refuses it server-side too — this is the display.
  const { data: pendingPayments } = usePendingPayments();
  const clearing = useMemo<InvoiceRow[]>(
    () => (invoices ?? []).filter((r) => r.isOpen && r.balance > 0 && pendingPayments?.has(r.qboInvoiceId)),
    [invoices, pendingPayments],
  );
  const payable = useMemo<InvoiceRow[]>(
    () => (invoices ?? []).filter((r) => r.isOpen && r.balance > 0 && !pendingPayments?.has(r.qboInvoiceId)),
    [invoices, pendingPayments],
  );

  const selectedRows = useMemo(() => payable.filter((r) => selected.has(r.qboInvoiceId)), [payable, selected]);
  const total = useMemo(() => Number(selectedRows.reduce((s, r) => s + r.balance, 0).toFixed(2)), [selectedRows]);
  const allSelected = payable.length > 0 && selected.size === payable.length;

  function payWithStripe() {
    if (!stripeMethod || selectedRows.length === 0) return;
    if (!window.confirm(actingPrefix(`Charge ${formatUsd(total)} to ${stripeMethodLabel(stripeMethod)}?`))) return;
    setStripeNotice(null);
    stripePay.mutate(
      { invoice_ids: selectedRows.map((r) => r.qboInvoiceId), stripe_pm_id: stripeMethod.stripe_pm_id },
      {
        onSuccess: (res) => {
          setStripeNotice(
            res.status === 'succeeded'
              ? `Payment of ${formatUsd(total)} received — your invoice${selectedRows.length === 1 ? '' : 's'} will show paid shortly.`
              : `Payment of ${formatUsd(total)} is processing — you'll get a receipt by email.`,
          );
          setSelected(new Set());
        },
      },
    );
  }

  // Deep-link pre-select: InvoiceDetail's "Pay this invoice" routes here as
  // /pay?invoice=<id> (also accepts ?invoices=id1,id2). Seed the selection once
  // the payable list is loaded, intersected with what's actually payable.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const raw = searchParams.get('invoice') ?? searchParams.get('invoices');
    if (!raw || payable.length === 0) return;
    const want = new Set(raw.split(',').map((s) => s.trim()).filter(Boolean));
    const payableIds = new Set(payable.map((r) => r.qboInvoiceId));
    const seed = [...want].filter((id) => payableIds.has(id));
    if (seed.length > 0) {
      setSelected(new Set(seed));
    }
    // Run once the payable list arrives; deliberately not re-seeding on every
    // selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payable.length]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(payable.map((r) => r.qboInvoiceId)));
  }

  if (group?.is_group) return <ChainPay />;

  // Hold the single-account render until we know which page this is — a flash
  // of the master's own few invoices before the chain list lands reads as
  // "where did everything go", which is exactly the complaint this fixes.
  if (groupLoading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-brix-muted">
        <Loader2 size={16} className="animate-spin inline-block mr-2" />
        Loading…
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-16 md:pb-10">
      <div className="px-6 md:px-8 pt-8 pb-12 max-w-3xl mx-auto">
        <h1 className="mb-4 text-center font-display text-2xl font-bold tracking-tight text-brix-ink">
          Pay
        </h1>
        <p className="text-center text-base mb-7 text-brix-muted max-w-2xl mx-auto">
          Select the invoices you'd like to pay and settle them together in one secure payment.
        </p>

        {clearing.length > 0 && (
          <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
            <div className="flex items-start gap-3">
              <Loader2 size={16} className="text-amber-700 mt-0.5 shrink-0 animate-spin" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-800">
                  {clearing.length === 1 ? 'A payment is' : `${clearing.length} payments are`} still clearing
                </p>
                <p className="text-xs text-amber-700/80 mt-0.5 leading-relaxed">
                  {processingNote(clearing.some((r) => pendingPayments?.get(r.qboInvoiceId)?.bank))}
                </p>
                <ul className="mt-2 text-xs text-amber-800 space-y-0.5">
                  {clearing.map((r) => (
                    <li key={r.qboInvoiceId}>
                      Invoice {r.docNumber ?? r.qboInvoiceId} · {formatUsd(r.balance)}
                      {pendingPayments?.get(r.qboInvoiceId)?.bank ? ' · bank payment, 3–5 business days' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="px-4 py-14 text-center text-sm text-brix-muted">
            <Loader2 size={18} className="animate-spin inline-block mr-2" /> Loading invoices…
          </div>
        ) : payable.length === 0 ? (
          <div className="rounded-2xl border border-brix-border bg-brix-surface/85 px-6 py-12 text-center">
            <CheckCircle2 size={28} className="text-emerald-600 mx-auto mb-2" />
            <p className="text-sm text-brix-muted">
              {clearing.length > 0 ? 'Nothing else to pay right now.' : "No open invoices to pay. You're all caught up."}
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl border-2 border-brix-primary/90 bg-brix-surface/85 backdrop-blur-xl overflow-hidden shadow-card-deep">
              <div className="h-1.5 bg-brix-primary" aria-hidden />
              <button
                type="button"
                onClick={toggleAll}
                className="w-full grid grid-cols-[24px_1fr_auto] sm:grid-cols-[28px_1fr_120px_130px] gap-3 px-4 sm:px-5 py-3 text-xs font-semibold tracking-tight text-brix-muted border-b border-brix-border bg-brix-surface-alt/60 items-center text-left"
              >
                <span className={cn('w-5 h-5 rounded border flex items-center justify-center', allSelected ? 'bg-brix-primary border-brix-primary text-white' : 'border-brix-border bg-brix-surface')}>
                  {allSelected && <CheckCircle2 size={13} />}
                </span>
                <span>{allSelected ? 'Deselect all' : 'Select all'}</span>
                <span className="hidden sm:block text-right">Due</span>
                <span className="text-right">Balance</span>
              </button>

              {payable.map((row) => {
                const on = selected.has(row.qboInvoiceId);
                const pastDue = (row.daysPastDue ?? 0) > 0;
                return (
                  <button
                    type="button"
                    key={row.qboInvoiceId}
                    onClick={() => toggle(row.qboInvoiceId)}
                    className={cn(
                      'w-full grid grid-cols-[24px_1fr_auto] sm:grid-cols-[28px_1fr_120px_130px] gap-3 px-4 sm:px-5 py-4 items-center text-left',
                      'border-b border-brix-border last:border-b-0 transition-colors',
                      on ? 'bg-brix-primary-tint/40' : 'hover:bg-brix-primary-tint/20',
                    )}
                  >
                    <span className={cn('w-5 h-5 rounded border flex items-center justify-center shrink-0', on ? 'bg-brix-primary border-brix-primary text-white' : 'border-brix-border bg-brix-surface')}>
                      {on && <CheckCircle2 size={13} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-base font-bold font-mono tracking-tight text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        #{row.docNumber ?? row.qboInvoiceId}
                      </span>
                      {row.memo && <span className="block text-xs text-slate-500 truncate max-w-[12rem] sm:max-w-[22rem]">{row.memo}</span>}
                      {/* Mobile: due date folded in (the Due column is hidden). */}
                      <span className="sm:hidden block text-[11px] text-slate-500 mt-0.5">
                        Due {fmtDate(row.dueDate)}{pastDue && <span className="text-red-600 font-semibold"> · Past due</span>}
                      </span>
                    </span>
                    <span className="hidden sm:block text-right text-sm">
                      <span className="block text-slate-900 font-medium">{fmtDate(row.dueDate)}</span>
                      {pastDue && <span className="block text-xs font-semibold text-red-600">Past due</span>}
                    </span>
                    <span className="text-right font-mono text-lg font-bold tracking-tight text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatUsd(row.balance)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Pay method + total */}
            <div className="mt-6 rounded-2xl border border-brix-border bg-brix-surface/85 backdrop-blur-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-slate-900">
                  {selected.size} selected
                </span>
                <span className="font-mono text-2xl font-bold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatUsd(total)}
                </span>
              </div>

              <div className="mb-3 space-y-2">
                <p className="text-sm font-bold text-brix-muted uppercase tracking-tight flex items-center gap-1.5">
                  Your saved cards &amp; banks
                  <InfoDot text="Add a card or bank in a secure form — encrypted by Stripe and never stored on our servers. Saving doesn't charge anything; it's kept for one-click payment." />
                </p>
                {stripeMethodsQuery.isLoading ? (
                  <p className="text-base text-brix-muted"><Loader2 size={15} className="animate-spin inline mr-1.5" />Loading saved methods…</p>
                ) : stripeMethods.length === 0 ? (
                  <p className="text-base text-slate-700 bg-brix-surface-alt/60 border border-brix-border rounded-lg px-3 py-2.5">
                    No payment method saved yet — add one below.
                  </p>
                ) : (
                  stripeMethods.map((m) => (
                    <button
                      type="button"
                      key={m.stripe_pm_id}
                      onClick={() => setStripeMethodId(m.stripe_pm_id)}
                      className={cn(
                        'w-full px-4 py-3.5 rounded-xl border text-left transition-all flex items-center gap-2.5',
                        stripeMethodId === m.stripe_pm_id
                          ? 'border-brix-primary bg-brix-primary-tint/50 ring-1 ring-brix-primary/40'
                          : 'border-brix-border bg-brix-surface hover:bg-brix-primary-tint/20',
                      )}
                    >
                      {m.kind === 'ach' ? <Banknote size={17} className="text-brix-primary shrink-0" /> : <CreditCard size={17} className="text-brix-primary shrink-0" />}
                      <span className="text-base font-bold text-slate-900">{stripeMethodLabel(m)}</span>
                      {m.is_default && <span className="text-xs text-brix-muted">main payer</span>}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); removeStripe.mutate(m.stripe_pm_id); }}
                        className="ml-auto text-sm text-red-600 hover:underline"
                      >
                        Remove
                      </span>
                    </button>
                  ))
                )}
                {pendingMethods.map((p) => (
                  <div
                    key={p.stripe_pm_id}
                    className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
                  >
                    <p className="flex flex-wrap items-center gap-2 text-base font-bold text-amber-900">
                      <Clock size={16} className="shrink-0" />
                      {stripeMethodLabel(p)}
                      <span className="rounded-md border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold">
                        Waiting to be confirmed
                      </span>
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-amber-900">
                      We can't pay with this bank until you confirm the small test deposit we sent
                      it.
                      {p.verification_url && (
                        <>
                          {' '}
                          <a href={p.verification_url} target="_blank" rel="noreferrer" className="font-bold underline">
                            Enter the code →
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                ))}
                <button type="button" onClick={() => navigate('/settings/payments')} className="text-sm font-bold text-brix-primary hover:underline">Manage payment methods →</button>
              </div>

              {stripeMethod?.kind === 'card' && total > CARD_NUDGE_OVER && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 flex items-start gap-1.5">
                  <Info size={13} className="mt-0.5 shrink-0" />
                  For a balance this size, paying by bank / ACH avoids card processing fees.
                </p>
              )}

              <div className="space-y-2">
                {stripePay.isError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{stripePay.error.message}</p>
                )}
                {stripeNotice && (
                  <p className="text-lg font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3.5 flex items-start gap-2">
                    <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                    {stripeNotice}
                  </p>
                )}
                {stripeMethods.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => navigate('/settings/payments')}
                    className="w-full px-5 py-3.5 rounded-xl bg-brix-primary text-white text-base font-semibold tracking-tight hover:bg-brix-primary-deep transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <CreditCard size={16} /> Set up a payment method →
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={payWithStripe}
                    disabled={selected.size === 0 || !stripeMethod || stripePay.isPending}
                    className="w-full px-5 py-3.5 rounded-xl bg-emerald-600 text-white text-base font-semibold tracking-tight disabled:opacity-40 hover:bg-emerald-700 transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {stripePay.isPending ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                    Pay {selected.size > 0 ? formatUsd(total) : 'payment'}{stripeMethod ? ` with ${stripeMethodLabel(stripeMethod)}` : ''}
                  </button>
                )}
              </div>
            </div>

            {/* The other way to pay: they push, we don't pull. Opens itself for
                a customer with nothing saved — that is exactly the person for
                whom this is the answer rather than a footnote — and for anyone
                arriving from the add-a-method picker's "I'll send payment
                myself", so that link can never land on a collapsed panel. */}
            <SelfPayPanel
              defaultOpen={
                searchParams.get('selfpay') === '1' ||
                (!stripeMethodsQuery.isLoading && stripeMethods.length === 0)
              }
            />

            {/* Sending us their own ACH? The details above are what they need,
                and this is the record of it — signed on screen, emailed to
                them for their vendor file, emailed to our remittance desk.
                Deliberately NOT auto-opened for everyone: a signature form
                that greets every visitor reads as something they must do
                before they can pay, which is the opposite of the truth. */}
            <AchAgreementCard defaultOpen={searchParams.get('achform') === '1'} />
          </>
        )}
      </div>
    </div>
  );
}
